import { ROUTES } from "@/enums/routes";
import { deleteMemberApi, getMembersApi } from "@/modules/organizationServices";
import {
  deleteMember,
  setInvitations,
  setMembers,
} from "@/store/redux/organization/memberSlice";
import { RootState } from "@/store/store";
import { Button } from "@/uiComponents/components/ui/button";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/uiComponents/components/ui/table";
import { IconPlus } from "@tabler/icons-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@radix-ui/react-dropdown-menu";
import {
  CircleAlert,
  EllipsisVertical,
  SquarePen,
  Trash2,
  X,
} from "lucide-react";
import AddMembers from "./AddMembers";
import { invitations, roles } from "@/enums/roles";
import UpdateMember from "./updateMember";
import { MemberErrors } from "@/enums/errorMessages";
import { Invitations, Member } from "@/types/organization";
import { ErrorHelper } from "@/utils/errorHelper";
import { getDisplayRole } from "@/utils/roleHelper";
import DeleteModal from "../DeleteModal";
import { toastMessage } from "@/utils/toastHelper";
import { TiTick } from "react-icons/ti";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/uiComponents/components/ui/tooltip";
import { AddExpenseButton } from "@/utils/AddExpenseButton";

const Members = () => {
  const navigate = useNavigate();
  const role = useSelector((state: RootState) => state.userDetail.role);
  const members = useSelector((state: RootState) => state.member.members);
  const invitationList = useSelector(
    (state: RootState) => state.member.invitations
  );
  const dispatch = useDispatch();
  const [isAddOpen, setIsAddOpen] = useState<boolean>(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState<boolean>(false);
  const [selectedMember, setSelectedMember] = useState<
    Member | Invitations | null
  >(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const usersData = [...members, ...invitationList];

  useEffect(() => {
    if (!role.isSuperAdmin) {
      navigate(ROUTES.DASHBOARD);
    }
  }, [role]);

  const isMember = (item: Member | Invitations): item is Member => {
    return "username" in item;
  };

  const handleDeleteMember = async (id?: number, email?: string) => {
    try {
      if (id) {
        await deleteMemberApi(role, id, email);
        dispatch(deleteMember(id));
      } else if (email) {
        await deleteMemberApi(role, id, email);
        dispatch(deleteMember(email));
      } else {
        throw new Error("No email or id provided for deletion");
      }
      toastMessage("success", MemberErrors.MEMBER_DELETE);
      await getMembers();
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };

  const getMembers = async () => {
    try {
      const res = await getMembersApi();
      const userResponse = res.users;
      const invitationResponse = res.invitations;

      if (userResponse != undefined) {
        dispatch(setMembers(userResponse));
      }
      if (invitationResponse != undefined) {
        dispatch(setInvitations(invitationResponse));
      }
    } catch (error) {
      const actualError = ErrorHelper(error);
      const message = Array.isArray(actualError) ? actualError[0] : actualError;
      toastMessage("error", message);
    }
  };
  useEffect(() => {
    getMembers();
  }, []);

  return (
    <div className="flex flex-1 flex-col max-w-screen md:ml-4 px-4 ">
      <div>
        <h2 className="text-2xl  font-semibold w-full">Members</h2>
        <AddMembers onClose={() => setIsAddOpen(false)} isOpen={isAddOpen} />
      </div>
      {selectedMember && isMember(selectedMember) && (
        <UpdateMember
          onClose={() => setIsUpdateOpen(false)}
          isOpen={isUpdateOpen}
          member={selectedMember}
        />
      )}
     <AddExpenseButton/>
      <div className="overflow-x-auto max-w-4xl mt-6 rounded-md ">
        <Table className="min-w-full bg-white border-1 ">
          <TableHeader>
            {(role.isSuperAdmin || role.isOrgAdmin) && (
              <TableRow>
                <TableCell className="text-right" colSpan={15}>
                  <Button
                   className=" bg-[#191F29]  hover:bg-[#2A313D] active:bg-[#001533]  cursor-pointer  whitespace-nowrap mt-2 mb-2 text-white hover:text-white"
                    onClick={() => setIsAddOpen(true)}
                    variant="outline"
                    size="sm"
                  >
                    <IconPlus />
                    <span className="hidden lg:inline ml-1">Invite Member</span>
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableHeader>

          <TableHeader>
            <TableRow className="font-medium">
              <TableCell>Username</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Branch</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {usersData.map((member) => (
              <TableRow key={member.id} className="h-12">
                <TableCell>{isMember(member) ? member.username : ""}</TableCell>
                <TableCell>{member.email}</TableCell>
                <TableCell>{member.branchName ?? "-"}</TableCell>

                <TableCell>
                  <span
                    className={`text-sm font-medium px-3 py-1 rounded-full ${getDisplayRole(member.role).className}`}
                  >
                    {getDisplayRole(member.role).label}
                  </span>
                </TableCell>
                <TableCell>
                  {member.status === invitations.USED ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <TiTick className="text-green-500" size={20} />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Used</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : member.status === invitations.PENDING ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CircleAlert className="text-yellow-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Pending</p>
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <X className="text-red-500" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Expired</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                {(role.isSuperAdmin || role.isOrgAdmin) &&
                  member.role !== roles.SUPER_ADMIN && (
                    <TableCell colSpan={10} className="text-right ">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            className=" w-10 p-0 cursor-pointer hover:bg-gray-200 "
                          >
                            <span className="sr-only">Open menu</span>
                            <EllipsisVertical className=" text-gray-700 text-xl " />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="w-32 rounded-md shadow-lg border bg-white"
                        >
                          <DropdownMenuItem
                            disabled={!isMember(member)}
                            onClick={() => {
                              setIsUpdateOpen(true);
                              setSelectedMember(member);
                            }}
                            className={`cursor-pointer ${!isMember(member) ? "text-gray-400 bg-gray-200 hover:bg-gray-200" : ""} text-left text-sm px-3 py-2`}
                          >
                            <div className="flex gap-2">
                              <SquarePen size={16} /> Edit
                            </div>
                          </DropdownMenuItem>
                          <div className="h-px bg-gray-200 my-0.5 w-full" />
                          <DropdownMenuItem
                            onClick={() => {
                              setIsDeleteOpen(true);
                              setSelectedMember(member);
                            }}
                            className="cursor-pointer text-left hover:bg-gray-300 text-sm text-gray-800 px-3 py-2"
                          >
                            <div className="flex gap-2">
                              <Trash2 size={16} /> Delete
                            </div>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {selectedMember && (
          <DeleteModal
            isOpen={isDeleteOpen}
            onClose={() => setIsDeleteOpen(false)}
            item={selectedMember}
            onConfirm={(item) => {
              if (isMember(item)) {
                handleDeleteMember(item.id, undefined);
              } else {
                handleDeleteMember(undefined, item.email);
              }
              setIsDeleteOpen(false);
              setSelectedMember(null);
            }}
            itemLabel={isMember(selectedMember) ? "member" : "invitation"}
          />
        )}
      </div>
    </div>
  );
};

export default Members;
